import React from "react";
import type { UseFormReturn } from "react-hook-form";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@/components";
import { RealCurrencyInput } from "@/components/ui/real-currency-input";
import { useI18n } from "@/context/i18n";

import type { TProductFormData } from "../../products.schema";
import {
  deriveProductPricing,
  formatProductPriceBrl,
} from "../../utils/soulhub-gateway-pricing.util";

export type PremiumPriceType = "net" | "commissioned" | undefined;

export interface ProductPricingSummaryFieldsProps {
  form: UseFormReturn<TProductFormData>;
  cfgPrefix: string;
  premiumPriceType?: PremiumPriceType;
  /** Quando true, o custo já aparece na seção Soulhub; exibir só venda e mínimo (fluxo NET). */
  hideCost?: boolean;
  /** Markup efetivamente aplicado (ex.: assistência NET força markup ligado). */
  markupEnabledEffective: boolean;
}

export const ProductPricingSummaryFields: React.FC<
  ProductPricingSummaryFieldsProps
> = ({
  form,
  cfgPrefix,
  premiumPriceType,
  hideCost = false,
  markupEnabledEffective,
}) => {
  const { t } = useI18n();

  const netPrice = form.watch(`${cfgPrefix}.net_price` as never);
  const gatewayPercent = form.watch(`${cfgPrefix}.gateway_fee_percent` as never);
  const gatewayPerSale = form.watch(`${cfgPrefix}.gateway_fee_per_sale` as never);
  const markupMode = form.watch(`${cfgPrefix}.markup_mode` as never);
  const markupValue = form.watch(`${cfgPrefix}.markup_value` as never);
  const commissionedPrice = form.watch(
    `${cfgPrefix}.commissioned_price` as never,
  );
  const commissionPercent = form.watch(
    `${cfgPrefix}.commission_percent` as never,
  );

  const isCommissioned = premiumPriceType === "commissioned";

  const { cost, salePrice } = React.useMemo(() => {
    if (isCommissioned) {
      return { cost: 0, salePrice: 0 };
    }
    const cfg = form.getValues(cfgPrefix as never) as Record<string, unknown>;
    return deriveProductPricing({
      ...cfg,
      markup_enabled: markupEnabledEffective,
    });
  }, [
    form,
    cfgPrefix,
    netPrice,
    gatewayPercent,
    gatewayPerSale,
    markupMode,
    markupValue,
    markupEnabledEffective,
    isCommissioned,
  ]);

  if (isCommissioned) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 rounded-md border border-dashed p-4 bg-muted/20">
        <FormItem>
          <FormLabel>{t("products.table.priceField.commissioned_price")}</FormLabel>
          <FormControl>
            <Input
              readOnly
              className="bg-muted"
              value={formatProductPriceBrl(Number(commissionedPrice ?? 0))}
            />
          </FormControl>
        </FormItem>
        <FormItem>
          <FormLabel>{t("products.table.priceField.commission_percent")}</FormLabel>
          <FormControl>
            <Input
              readOnly
              className="bg-muted"
              value={
                commissionPercent === undefined || commissionPercent === null
                  ? "—"
                  : `${Number(commissionPercent)}%`
              }
            />
          </FormControl>
        </FormItem>
        <FormField
          control={form.control}
          name={`${cfgPrefix}.minimum_price` as never}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("products.pricing.minimum_price_product")}</FormLabel>
              <FormControl>
                <RealCurrencyInput
                  value={Number(field.value ?? 0)}
                  onChange={field.onChange}
                />
              </FormControl>
              <p className="text-xs text-muted-foreground">
                {t("products.pricing.minimum_editable_hint")}
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    );
  }

  return (
    <div
      className={
        hideCost
          ? "grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border border-dashed p-4 bg-muted/20"
          : "grid grid-cols-1 md:grid-cols-3 gap-4 rounded-md border border-dashed p-4 bg-muted/20"
      }
    >
      {!hideCost ? (
        <FormItem>
          <FormLabel>{t("products.pricing.cost_price")}</FormLabel>
          <FormControl>
            <Input
              readOnly
              className="bg-muted"
              value={formatProductPriceBrl(cost)}
            />
          </FormControl>
        </FormItem>
      ) : null}
      <FormItem>
        <FormLabel>{t("products.pricing.sale_price_calculated")}</FormLabel>
        <FormControl>
          <Input
            readOnly
            className="bg-muted"
            value={formatProductPriceBrl(salePrice)}
          />
        </FormControl>
      </FormItem>
      <FormField
        control={form.control}
        name={`${cfgPrefix}.minimum_price` as never}
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t("products.pricing.minimum_price_product")}</FormLabel>
            <FormControl>
              <RealCurrencyInput
                value={Number(field.value ?? 0)}
                onChange={field.onChange}
              />
            </FormControl>
            <p className="text-xs text-muted-foreground">
              {t("products.pricing.minimum_editable_hint")}
            </p>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
